import { useState, useCallback, useEffect, useRef } from 'react';
import api from '../../api';
import {
  ACTIVATION_POLL_INTERVAL_MS,
  parseActivityRows,
  remapPortStatusForPorts,
} from './parseActivityRows';

const ADAPTIVE_POLL_ACTIVE_MS = 10000;
const ADAPTIVE_POLL_IDLE_MS = 120000;

/**
 * Single hook for port **activation** status (hardware-first, queue fail-safe).
 */
export function usePortActivationStatus({
  scope = 'staff',
  gatewayId = null,
  pollIntervalMs = ACTIVATION_POLL_INTERVAL_MS,
  adaptivePoll = false,
  remapPorts = null,
  enabled = true,
} = {}) {
  const [portStatus, setPortStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const fetchingRef = useRef(false);
  const portStatusRef = useRef(portStatus);

  useEffect(() => {
    portStatusRef.current = portStatus;
  }, [portStatus]);

  const endpoint = scope === 'staff' ? '/port-activity/staff' : '/port-activity/me';

  const refresh = useCallback(async () => {
    if (!enabled || fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const params = { t: Date.now() };
      if (gatewayId != null && scope === 'me') {
        params.gateway_id = gatewayId;
      }
      const res = await api.get(endpoint, { params });
      let map = parseActivityRows(res.data);
      if (remapPorts?.length) {
        map = remapPortStatusForPorts(map, remapPorts);
      }
      setPortStatus(map);
      setLastUpdated(new Date());
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'Failed to load activation status'
      );
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [enabled, endpoint, gatewayId, remapPorts, scope]);

  // Initial load + regular polling
  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    if (adaptivePoll) return undefined;

    const id = setInterval(refresh, pollIntervalMs);
    return () => clearInterval(id);
  }, [enabled, refresh, pollIntervalMs, adaptivePoll]);

  // Adaptive polling: faster while activations in-flight
  useEffect(() => {
    if (!enabled || !adaptivePoll) return undefined;

    const tick = () => {
      const hasActiveOps = Object.values(portStatusRef.current).some(
        (s) => s?.status === 'activating' || s?.status === 'queued'
      );
      return hasActiveOps ? ADAPTIVE_POLL_ACTIVE_MS : ADAPTIVE_POLL_IDLE_MS;
    };

    let timer = null;
    const schedule = () => {
      timer = setTimeout(async () => {
        await refresh();
        schedule();
      }, tick());
    };
    schedule();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [enabled, adaptivePoll, refresh]);

  // WebSocket: refresh when hardware cache or queue changes
  useEffect(() => {
    if (!enabled) return undefined;
    const unsubActivity = api.subscribeTo('port_activity_update', () => {
      refresh();
    });
    const unsubActivation = api.subscribeTo('activation_update', () => {
      refresh();
    });
    return () => {
      if (unsubActivity) unsubActivity();
      if (unsubActivation) unsubActivation();
    };
  }, [enabled, refresh]);

  return {
    portStatus,
    loading,
    error,
    lastUpdated,
    refresh,
  };
}
